/**
 * Whole-sell listing title formation engine.
 *
 * Owns canonical slot order, join/truncate, and positive-only tech-flag tokens.
 * Family adapters and the default simple map only supply slot values — not policy.
 *
 * Axis/filter labels that need "Non-Inverter" / "Non-Smart" stay on
 * `formatInverterTitleWord` / `formatMicrowaveSmartTitleWord` in
 * `applianceInverterListingSpec.ts` (D6).
 */

/** Buyer-facing listing title max length (sell + persist). */
export const LISTING_TITLE_MAX_LENGTH = 60;

/**
 * Canonical title slot order (D1–D3, D9–D10).
 * Empty slots are dropped by {@link joinListingTitleParts}.
 */
export const LISTING_TITLE_SLOT_ORDER = [
  "capacity",
  "inverter",
  "smart",
  "form",
  "brand",
  "model",
  "product",
  "extras",
] as const;

export type ListingTitleSlotId = (typeof LISTING_TITLE_SLOT_ORDER)[number];

export type ListingTitleSlotValues = Partial<
  Record<ListingTitleSlotId, string | number | null | undefined>
>;

/** Join title tokens, dropping empty slots. */
export function joinListingTitleParts(
  ...parts: Array<string | number | null | undefined>
): string {
  return parts
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter((part) => part.length > 0)
    .join(" ");
}

export function truncateListingTitle(title: string): string {
  const trimmed = title.trim();
  if (trimmed.length <= LISTING_TITLE_MAX_LENGTH) {
    return trimmed;
  }
  return trimmed.slice(0, LISTING_TITLE_MAX_LENGTH).trim();
}

/**
 * Positive-only inverter title token (D4).
 * Returns "" for non-inverter / unknown — never "Non-Inverter".
 */
export function inverterTitleToken(
  value: string | null | undefined,
): "Inverter" | "" {
  return value === "inverter" ? "Inverter" : "";
}

/**
 * Positive-only smart title token (D4).
 * Returns "" for non-smart / unknown — never "Non-Smart".
 */
export function smartTitleToken(
  value: string | null | undefined,
): "Smart" | "" {
  return value === "smart" ? "Smart" : "";
}

/** Compose a truncated title from canonical slots in locked order. */
export function composeListingTitleFromSlots(
  slots: ListingTitleSlotValues,
): string {
  return truncateListingTitle(
    joinListingTitleParts(
      ...LISTING_TITLE_SLOT_ORDER.map((slotId) => slots[slotId]),
    ),
  );
}

/** Sell-form identity (brand / model) — merged once for all patterns (D10 / Phase 2.1). */
export type ListingTitleIdentity = {
  brand?: string | null;
  model?: string | null;
};

/**
 * Merge seller brand/model into slots. Non-empty identity wins; empty identity
 * leaves existing slot values unchanged.
 */
export function withListingTitleIdentity(
  slots: ListingTitleSlotValues,
  identity?: ListingTitleIdentity | null,
): ListingTitleSlotValues {
  const brand = identity?.brand?.trim();
  const model = identity?.model?.trim();
  return {
    ...slots,
    ...(brand ? { brand } : {}),
    ...(model ? { model } : {}),
  };
}

/** Compose slots after applying sell-form identity (brand / model). */
export function composeListingTitleFromSlotsWithIdentity(
  slots: ListingTitleSlotValues,
  identity?: ListingTitleIdentity | null,
): string {
  return composeListingTitleFromSlots(
    withListingTitleIdentity(slots, identity),
  );
}
